import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Bot, CreditCard, Settings, Plug, Rocket, ChevronRight, ChevronLeft,
  Check, Sparkles, Shield, Zap, Clock, Star, Users, Globe, MessageSquare,
  Mail, Webhook, Slack, Phone, ArrowRight, CheckCircle2, XCircle, Loader2,
  Pause, Play, Trash2, RotateCcw, ExternalLink, Copy, Eye, EyeOff,
  AlertTriangle, Info, X, Plus, Minus, Search, ChevronDown,
} from "lucide-react";
import { toast } from "sonner";

// ─── Step Indicator ───────────────────────────────────────────────────────────
function StepIndicator({ currentStep, completedSteps, onStepClick }) {
  const { t } = useTranslation();
  const steps = [
    { num: 1, label: t("deploy.step_1", "Agent Selection"), icon: Bot },
    { num: 2, label: t("deploy.step_2", "Plan Selection"), icon: CreditCard },
    { num: 3, label: t("deploy.step_3", "Configuration"), icon: Settings },
    { num: 4, label: t("deploy.step_4", "Channel Setup"), icon: Plug },
    { num: 5, label: t("deploy.step_5", "Review & Deploy"), icon: Rocket },
  ];

  return (
    <div className="flex items-center justify-between mb-4 sm:mb-8 px-0 sm:px-2">
      {steps.map((step, i) => {
        const Icon = step.icon;
        const isActive = currentStep === step.num;
        const isCompleted = completedSteps.includes(step.num);
        const isClickable = isCompleted || completedSteps.includes(step.num - 1) || step.num === 1;

        return (
          <div key={step.num} className="flex items-center flex-1 last:flex-none">
            <button
              onClick={() => isClickable && onStepClick(step.num)}
              disabled={!isClickable}
              className={`flex items-center gap-1 sm:gap-2 px-1 sm:px-3 py-2 transition-all ${
                isActive
                  ? "text-amber-400"
                  : isCompleted
                  ? "text-emerald-400"
                  : "text-zinc-600"
              } ${isClickable ? "cursor-pointer hover:text-amber-300" : "cursor-not-allowed"}`}
            >
              <div
                className={`w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center border text-xs font-mono font-bold transition-all ${
                  isActive
                    ? "border-amber-400 bg-amber-400/10 text-amber-400"
                    : isCompleted
                    ? "border-emerald-400 bg-emerald-400/10 text-emerald-400"
                    : "border-zinc-700 text-zinc-600"
                }`}
              >
                {isCompleted ? <Check className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
              </div>
              <span className="hidden sm:block font-mono text-xs uppercase tracking-wider">
                {step.label}
              </span>
            </button>
            {i < steps.length - 1 && (
              <div
                className={`flex-1 h-px mx-2 ${
                  completedSteps.includes(step.num) ? "bg-emerald-400/30" : "bg-zinc-800"
                }`}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Step 1: Agent Selection ─────────────────────────────────────────────────
function AgentSelection({ selectedAgent, onSelect, onNext }) {
  const { t } = useTranslation();

  const agents = [
    {
      id: "general",
      name: t("deploy.agent_general", "General Operations"),
      description: t("deploy.agent_general_desc", "Versatile AI agent for general business operations, customer support, and task automation."),
      capabilities: ["Customer Support", "Task Automation", "Data Entry", "Email Management", "Scheduling"],
      icon: Bot,
      color: "amber",
    },
    {
      id: "sales",
      name: t("deploy.agent_sales", "Sales Agent"),
      description: t("deploy.agent_sales_desc", "Specialized in lead qualification, follow-ups, and sales pipeline management."),
      capabilities: ["Lead Qualification", "Follow-ups", "Pipeline Management", "CRM Integration", "Proposal Generation"],
      icon: Users,
      color: "emerald",
    },
    {
      id: "support",
      name: t("deploy.agent_support", "Support Agent"),
      description: t("deploy.agent_support_desc", "Dedicated to customer support, ticket resolution, and FAQ handling."),
      capabilities: ["Ticket Resolution", "FAQ Handling", "Escalation", "Multi-language", "Knowledge Base"],
      icon: MessageSquare,
      color: "blue",
    },
    {
      id: "marketing",
      name: t("deploy.agent_marketing", "Marketing Agent"),
      description: t("deploy.agent_marketing_desc", "Content creation, social media management, and campaign tracking."),
      capabilities: ["Content Creation", "Social Media", "Campaign Tracking", "Analytics", "A/B Testing"],
      icon: Globe,
      color: "purple",
    },
  ];

  const colorMap = {
    amber: { border: "border-amber-500/30", bg: "bg-amber-500/10", text: "text-amber-400", badge: "bg-amber-500/20 text-amber-400" },
    emerald: { border: "border-emerald-500/30", bg: "bg-emerald-500/10", text: "text-emerald-400", badge: "bg-emerald-500/20 text-emerald-400" },
    blue: { border: "border-blue-500/30", bg: "bg-blue-500/10", text: "text-blue-400", badge: "bg-blue-500/20 text-blue-400" },
    purple: { border: "border-purple-500/30", bg: "bg-purple-500/10", text: "text-purple-400", badge: "bg-purple-500/20 text-purple-400" },
  };

  return (
    <div>
      <div className="mb-6">
        <h2 className="font-display text-xl sm:text-2xl text-white mb-2">
          {t("deploy.select_agent", "Select Your Agent")}
        </h2>
        <p className="font-mono text-xs text-zinc-500">
          {t("deploy.select_agent_desc", "Choose the type of AI agent that best fits your business needs.")}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        {agents.map((agent) => {
          const Icon = agent.icon;
          const colors = colorMap[agent.color];
          const isSelected = selectedAgent?.id === agent.id;

          return (
            <button
              key={agent.id}
              onClick={() => onSelect(agent)}
              className={`text-left p-4 sm:p-5 border transition-all ${
                isSelected
                  ? `${colors.border} ${colors.bg}`
                  : "border-zinc-800 bg-zinc-950/30 hover:border-zinc-700"
              }`}
            >
              <div className="flex items-start gap-3 mb-3">
                <div className={`w-10 h-10 flex items-center justify-center ${colors.bg}`}>
                  <Icon className={`w-5 h-5 ${colors.text}`} />
                </div>
                <div>
                  <h3 className="font-display text-white text-sm">{agent.name}</h3>
                  <p className="font-mono text-[11px] text-zinc-500 mt-1 leading-relaxed">
                    {agent.description}
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {agent.capabilities.map((cap) => (
                  <span
                    key={cap}
                    className={`font-mono text-[9px] sm:text-[10px] px-2 py-0.5 ${colors.badge}`}
                  >
                    {cap}
                  </span>
                ))}
              </div>
              {isSelected && (
                <div className="mt-3 flex items-center gap-1.5 text-emerald-400">
                  <CheckCircle2 className="w-4 h-4" />
                  <span className="font-mono text-[11px] uppercase tracking-wider">
                    {t("deploy.selected", "Selected")}
                  </span>
                </div>
              )}
            </button>
          );
        })}
      </div>

      <div className="flex justify-end w-full sm:w-auto">
        <button
          onClick={onNext}
          disabled={!selectedAgent}
          className="flex items-center gap-2 w-full sm:w-auto px-6 py-3 bg-amber-500 text-black font-mono text-sm font-bold uppercase tracking-wider hover:bg-amber-400 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
        >
          {t("deploy.continue", "Continue")}
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

// ─── Step 2: Plan Selection ──────────────────────────────────────────────────
function PlanSelection({ selectedPlan, billingCycle, promoCode, onSelectPlan, onToggleBilling, onPromoChange, onNext, onBack }) {
  const { t } = useTranslation();

  const plans = [
    {
      id: "starter",
      name: t("deploy.plan_starter", "Starter"),
      monthlyPrice: 49,
      annualPrice: 39,
      description: t("deploy.plan_starter_desc", "Perfect for small businesses getting started with AI automation."),
      features: [
        t("deploy.starter_f1", "1 AI agent"),
        t("deploy.starter_f2", "1 channel (WhatsApp, Telegram, or Email)"),
        t("deploy.starter_f3", "5,000 messages/month"),
        t("deploy.starter_f4", "Basic dashboard"),
        t("deploy.starter_f5", "Email support"),
        t("deploy.starter_f6", "7-day free trial"),
      ],
      table: { channels: "1", messages: "5,000", dashboard: t("deploy.basic", "Basic"), sla: "—", api: false, skills: false, onboarding: false, trial: true },
      featured: false,
      spotsLeft: 12,
    },
    {
      id: "pro",
      name: t("deploy.plan_pro", "Pro"),
      monthlyPrice: 149,
      annualPrice: 119,
      description: t("deploy.plan_pro_desc", "For growing teams that need more channels and advanced features."),
      features: [
        t("deploy.pro_f1", "1 AI agent"),
        t("deploy.pro_f2", "3 channels"),
        t("deploy.pro_f3", "25,000 messages/month"),
        t("deploy.pro_f4", "Full dashboard + analytics"),
        t("deploy.pro_f5", "Priority email support"),
        t("deploy.pro_f6", "Custom agent skills"),
        t("deploy.pro_f7", "API access"),
        t("deploy.pro_f8", "7-day free trial"),
      ],
      table: { channels: "3", messages: "25,000", dashboard: t("deploy.full_analytics", "Full + analytics"), sla: "48h", api: true, skills: true, onboarding: false, trial: true },
      featured: true,
      spotsLeft: 7,
    },
    {
      id: "enterprise",
      name: t("deploy.plan_enterprise", "Enterprise"),
      monthlyPrice: 499,
      annualPrice: 399,
      description: t("deploy.plan_enterprise_desc", "Full-scale AI infrastructure for large organizations."),
      features: [
        t("deploy.ent_f1", "Up to 5 AI agents"),
        t("deploy.ent_f2", "Unlimited channels"),
        t("deploy.ent_f3", "100,000 messages/month"),
        t("deploy.ent_f4", "Full dashboard + analytics + API"),
        t("deploy.ent_f5", "Priority support (4h SLA)"),
        t("deploy.ent_f6", "Custom integrations"),
        t("deploy.ent_f7", "Custom agent skills"),
        t("deploy.ent_f8", "Dedicated onboarding call"),
      ],
      table: { channels: t("deploy.unlimited", "Unlimited"), messages: "100,000", dashboard: t("deploy.full_api", "Full + API"), sla: "4h", api: true, skills: true, onboarding: true, trial: true },
      featured: false,
      spotsLeft: 3,
    },
  ];

  const isAnnual = billingCycle === "annual";
  const promoDiscount = promoCode === "MAX2026" ? 0.2 : 0;

  return (
    <div>
      <div className="mb-6">
        <h2 className="font-display text-xl sm:text-2xl text-white mb-2">
          {t("deploy.select_plan", "Select Your Plan")}
        </h2>
        <p className="font-mono text-xs text-zinc-500">
          {t("deploy.select_plan_desc", "Choose the plan that fits your team size and usage needs.")}
        </p>
      </div>

      {/* Billing Toggle */}
      <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-4 mb-8">
        <span className={`font-mono text-xs sm:text-sm ${!isAnnual ? "text-white" : "text-zinc-500"}`}>
          {t("deploy.monthly", "Monthly")}
        </span>
        <button
          onClick={onToggleBilling}
          className={`relative w-12 h-6 transition-colors ${isAnnual ? "bg-amber-500" : "bg-zinc-700"}`}
        >
          <div
            className={`absolute top-0.5 w-5 h-5 bg-white transition-transform ${
              isAnnual ? "translate-x-6" : "translate-x-0.5"
            }`}
          />
        </button>
        <span className={`font-mono text-xs sm:text-sm ${isAnnual ? "text-amber-400" : "text-zinc-500"}`}>
          {t("deploy.annual", "Annual")}
        </span>
        {isAnnual && (
          <span className="font-mono text-[10px] px-2 py-1 bg-emerald-500/20 text-emerald-400 uppercase tracking-wider">
            {t("deploy.save_20", "SAVE 20%")}
          </span>
        )}
      </div>

      {/* Plan Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        {plans.map((plan) => {
          const price = isAnnual ? plan.annualPrice : plan.monthlyPrice;
          const finalPrice = Math.round(price * (1 - promoDiscount));
          const isSelected = selectedPlan?.id === plan.id;

          return (
            <button
              key={plan.id}
              onClick={() => onSelectPlan(plan)}
              className={`text-left p-4 sm:p-5 border transition-all relative ${
                isSelected
                  ? "border-amber-500/50 bg-amber-500/5"
                  : plan.featured
                  ? "border-zinc-700 bg-zinc-950/50 hover:border-zinc-600"
                  : "border-zinc-800 bg-zinc-950/30 hover:border-zinc-700"
              }`}
            >
              {plan.featured && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-amber-500 text-black font-mono text-[10px] uppercase tracking-wider font-bold">
                  {t("deploy.most_popular", "MOST POPULAR")}
                </div>
              )}

              <h3 className="font-display text-lg text-white mb-1">{plan.name}</h3>
              <p className="font-mono text-[11px] text-zinc-500 mb-4">{plan.description}</p>

              <div className="mb-4">
                <span className="font-display text-2xl sm:text-3xl text-white">${finalPrice}</span>
                <span className="font-mono text-xs text-zinc-500">
                  /{t("deploy.month", "mo")}
                </span>
                {promoDiscount > 0 && (
                  <div className="font-mono text-[10px] text-emerald-400 mt-1">
                    {t("deploy.promo_applied", "Promo applied!")} -{promoDiscount * 100}%
                  </div>
                )}
              </div>

              <div className="font-mono text-[10px] text-amber-400/70 mb-4 flex items-center gap-1">
                <Zap className="w-3 h-3" />
                {t("deploy.spots_left", "{{count}} spots left", { count: plan.spotsLeft })}
              </div>

              <ul className="space-y-2">
                {plan.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <Check className="w-3.5 h-3.5 text-emerald-400 mt-0.5 shrink-0" />
                    <span className="font-mono text-[11px] text-zinc-400">{f}</span>
                  </li>
                ))}
              </ul>

              {isSelected && (
                <div className="absolute top-3 right-3">
                  <CheckCircle2 className="w-5 h-5 text-amber-400" />
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Promo Code */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 mb-6 p-4 border border-zinc-800 bg-zinc-950/30">
        <input
          type="text"
          value={promoCode}
          onChange={(e) => onPromoChange(e.target.value)}
          placeholder={t("deploy.promo_placeholder", "Enter promo code")}
          className="flex-1 bg-transparent border border-zinc-700 px-3 py-2 font-mono text-sm text-white placeholder:text-zinc-600 focus:border-amber-500/50 focus:outline-none"
        />
        <button
          onClick={() => {
            if (promoCode === "MAX2026") {
              toast.success(t("deploy.promo_valid", "Promo code applied! 20% discount."));
            } else if (promoCode) {
              toast.error(t("deploy.promo_invalid", "Invalid promo code."));
            }
          }}
          className="w-full sm:w-auto px-4 py-2 border border-zinc-700 font-mono text-xs text-zinc-400 uppercase tracking-wider hover:border-amber-500/30 hover:text-amber-400 transition-colors"
        >
          {t("deploy.apply", "Apply")}
        </button>
      </div>

      {/* Feature Comparison Table */}
      <div className="mb-6 overflow-x-auto">
        <table className="w-full text-left">
          <thead>
            <tr className="border-b border-zinc-800">
              <th className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 pb-3 pr-4">
                {t("deploy.feature", "Feature")}
              </th>
              {plans.map((p) => (
                <th key={p.id} className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 pb-3 px-4 text-center">
                  {p.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="font-mono text-[11px]">
            {[
              { label: t("deploy.channels", "Channels"), key: "channels" },
              { label: t("deploy.messages", "Messages/month"), key: "messages" },
              { label: t("deploy.dashboard", "Dashboard"), key: "dashboard" },
              { label: t("deploy.support_sla", "Support SLA"), key: "sla" },
              { label: t("deploy.api_access", "API access"), key: "api" },
              { label: t("deploy.custom_skills", "Custom skills"), key: "skills" },
              { label: t("deploy.onboarding_call", "Onboarding call"), key: "onboarding" },
              { label: t("deploy.free_trial", "Free trial"), key: "trial" },
            ].map((row) => (
              <tr key={row.key} className="border-b border-zinc-800/50">
                <td className="py-2.5 pr-4 text-zinc-400">{row.label}</td>
                {plans.map((p) => (
                  <td key={p.id} className="py-2.5 px-4 text-center">
                    {typeof p.table[row.key] === "boolean" ? (
                      p.table[row.key] ? (
                        <Check className="w-3.5 h-3.5 text-emerald-400 mx-auto" />
                      ) : (
                        <X className="w-3.5 h-3.5 text-zinc-600 mx-auto" />
                      )
                    ) : (
                      <span className="text-zinc-300">{p.table[row.key]}</span>
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex flex-col sm:flex-row justify-between gap-2">
        <button
          onClick={onBack}
          className="flex items-center gap-2 px-6 py-3 border border-zinc-700 font-mono text-sm text-zinc-400 uppercase tracking-wider hover:border-zinc-600 transition-colors"
        >
          <ChevronLeft className="w-4 h-4" />
          {t("deploy.back", "Back")}
        </button>
        <button
          onClick={onNext}
          disabled={!selectedPlan}
          className="flex items-center gap-2 px-6 py-3 bg-amber-500 text-black font-mono text-sm font-bold uppercase tracking-wider hover:bg-amber-400 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
        >
          {t("deploy.continue", "Continue")}
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

// ─── Step 3: Agent Configuration ─────────────────────────────────────────────
function AgentConfiguration({ config, onChange, onNext, onBack }) {
  const { t } = useTranslation();

  const personalities = [
    { id: "professional", label: t("deploy.pers_professional", "Professional"), icon: "👔" },
    { id: "friendly", label: t("deploy.pers_friendly", "Friendly"), icon: "😊" },
    { id: "technical", label: t("deploy.pers_technical", "Technical"), icon: "🔧" },
    { id: "casual", label: t("deploy.pers_casual", "Casual"), icon: "✌️" },
  ];

  const timezones = [
    "UTC", "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles",
    "America/Sao_Paulo", "America/Argentina/Buenos_Aires", "Europe/London", "Europe/Paris",
    "Europe/Berlin", "Europe/Madrid", "Asia/Tokyo", "Asia/Shanghai", "Asia/Singapore",
    "Australia/Sydney", "Africa/Johannesburg", "Pacific/Auckland",
  ];

  const languages = [
    { code: "en", label: "English" },
    { code: "pt", label: "Português" },
    { code: "es", label: "Español" },
    { code: "fr", label: "Français" },
    { code: "de", label: "Deutsch" },
    { code: "it", label: "Italiano" },
    { code: "ja", label: "日本語" },
    { code: "zh", label: "中文" },
    { code: "ko", label: "한국어" },
  ];

  const [tzSearch, setTzSearch] = useState("");
  const [tzOpen, setTzOpen] = useState(false);
  const filteredTimezones = timezones.filter((tz) =>
    tz.toLowerCase().includes(tzSearch.toLowerCase())
  );

  const nameError = config.agentName && config.agentName.length < 3
    ? t("deploy.name_error", "Agent name must be at least 3 characters.")
    : null;

  return (
    <div>
      <div className="mb-6">
        <h2 className="font-display text-xl sm:text-2xl text-white mb-2">
          {t("deploy.configure_agent", "Configure Your Agent")}
        </h2>
        <p className="font-mono text-xs text-zinc-500">
          {t("deploy.configure_agent_desc", "Customize your agent's identity, personality, and preferences.")}
        </p>
      </div>

      <div className="space-y-6">
        {/* Agent Name */}
        <div>
          <label className="block font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-2">
            {t("deploy.agent_name", "Agent Name")} *
          </label>
          <input
            type="text"
            value={config.agentName || ""}
            onChange={(e) => onChange({ ...config, agentName: e.target.value })}
            placeholder={t("deploy.agent_name_placeholder", "e.g., Support Bot, Sales Assistant")}
            className={`w-full bg-transparent border px-4 py-3 font-mono text-sm text-white placeholder:text-zinc-600 focus:outline-none ${
              nameError ? "border-red-500/50" : "border-zinc-700 focus:border-amber-500/50"
            }`}
          />
          {nameError && (
            <p className="font-mono text-[10px] text-red-400 mt-1 flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" /> {nameError}
            </p>
          )}
        </div>

        {/* Avatar Selection */}
        <div>
          <label className="block font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-2">
            {t("deploy.avatar", "Avatar")}
          </label>
          <div className="flex items-center gap-3">
            <div className="w-16 h-16 border border-zinc-700 flex items-center justify-center text-3xl bg-zinc-950/50">
              {config.avatar || "🤖"}
            </div>
            <div className="flex flex-wrap gap-2">
              {["🤖", "👨‍💼", "👩‍💼", "🧑‍💻", "👽", "🦊", "🐱", "🦁", "🐼", "🦄"].map((emoji) => (
                <button
                  key={emoji}
                  onClick={() => onChange({ ...config, avatar: emoji })}
                  className={`w-10 h-10 flex items-center justify-center text-lg border transition-colors ${
                    config.avatar === emoji
                      ? "border-amber-500 bg-amber-500/10"
                      : "border-zinc-700 hover:border-zinc-600"
                  }`}
                >
                  {emoji}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Personality */}
        <div>
          <label className="block font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-2">
            {t("deploy.personality", "Personality")}
          </label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
            {personalities.map((p) => (
              <button
                key={p.id}
                onClick={() => onChange({ ...config, personality: p.id })}
                className={`p-2 sm:p-3 border text-center transition-colors ${
                  config.personality === p.id
                    ? "border-amber-500/50 bg-amber-500/10"
                    : "border-zinc-800 hover:border-zinc-700"
                }`}
              >
                <div className="text-2xl mb-2">{p.icon}</div>
                <div className="font-mono text-[11px] text-zinc-300">{p.label}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Timezone */}
        <div className="relative">
          <label className="block font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-2">
            {t("deploy.timezone", "Timezone")}
          </label>
          <button
            onClick={() => setTzOpen(!tzOpen)}
            className="w-full flex items-center justify-between bg-transparent border border-zinc-700 px-4 py-3 font-mono text-sm text-white focus:border-amber-500/50 focus:outline-none"
          >
            <span>{config.timezone || t("deploy.select_timezone", "Select timezone...")}</span>
            <ChevronDown className="w-4 h-4 text-zinc-500" />
          </button>
          {tzOpen && (
            <div className="absolute z-10 w-full mt-1 border border-zinc-700 bg-zinc-950 max-h-48 overflow-y-auto">
              <div className="p-2">
                <input
                  type="text"
                  value={tzSearch}
                  onChange={(e) => setTzSearch(e.target.value)}
                  placeholder={t("deploy.search_timezone", "Search...")}
                  className="w-full bg-transparent border border-zinc-700 px-3 py-1.5 font-mono text-xs text-white placeholder:text-zinc-600 focus:outline-none"
                />
              </div>
              {filteredTimezones.map((tz) => (
                <button
                  key={tz}
                  onClick={() => {
                    onChange({ ...config, timezone: tz });
                    setTzOpen(false);
                    setTzSearch("");
                  }}
                  className={`w-full text-left px-4 py-2 font-mono text-xs hover:bg-zinc-800 ${
                    config.timezone === tz ? "text-amber-400" : "text-zinc-400"
                  }`}
                >
                  {tz}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Languages */}
        <div>
          <label className="block font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-2">
            {t("deploy.languages", "Languages")}
          </label>
          <div className="flex flex-wrap gap-2">
            {languages.map((lang) => {
              const isSelected = (config.languages || []).includes(lang.code);
              return (
                <button
                  key={lang.code}
                  onClick={() => {
                    const current = config.languages || [];
                    const updated = isSelected
                      ? current.filter((c) => c !== lang.code)
                      : [...current, lang.code];
                    onChange({ ...config, languages: updated });
                  }}
                  className={`px-3 py-1.5 font-mono text-[11px] border transition-colors ${
                    isSelected
                      ? "border-amber-500/50 bg-amber-500/10 text-amber-400"
                      : "border-zinc-700 text-zinc-400 hover:border-zinc-600"
                  }`}
                >
                  {isSelected && <Check className="w-3 h-3 inline mr-1" />}
                  {lang.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Custom Greeting */}
        <div>
          <label className="block font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-2">
            {t("deploy.greeting", "Custom Greeting Message")}
          </label>
          <textarea
            value={config.greeting || ""}
            onChange={(e) => onChange({ ...config, greeting: e.target.value })}
            placeholder={t("deploy.greeting_placeholder", "e.g., Hello! I'm your AI assistant. How can I help you today?")}
            rows={3}
            className="w-full bg-transparent border border-zinc-700 px-4 py-3 font-mono text-sm text-white placeholder:text-zinc-600 focus:border-amber-500/50 focus:outline-none resize-none"
          />
        </div>
      </div>

      <div className="flex flex-col sm:flex-row justify-between gap-2 mt-4 sm:mt-8">
        <button
          onClick={onBack}
          className="flex items-center gap-2 px-6 py-3 border border-zinc-700 font-mono text-sm text-zinc-400 uppercase tracking-wider hover:border-zinc-600 transition-colors"
        >
          <ChevronLeft className="w-4 h-4" />
          {t("deploy.back", "Back")}
        </button>
        <button
          onClick={onNext}
          disabled={!config.agentName || config.agentName.length < 3}
          className="flex items-center gap-2 px-6 py-3 bg-amber-500 text-black font-mono text-sm font-bold uppercase tracking-wider hover:bg-amber-400 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
        >
          {t("deploy.continue", "Continue")}
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

// ─── Step 4: Channel Setup ───────────────────────────────────────────────────
function ChannelSetup({ channels, onToggleChannel, onTestChannel, onNext, onBack }) {
  const { t } = useTranslation();

  const channelList = [
    {
      id: "telegram",
      name: "Telegram",
      icon: MessageSquare,
      color: "text-blue-400",
      bg: "bg-blue-500/10",
      border: "border-blue-500/30",
      description: t("deploy.telegram_desc", "Connect via Bot API token"),
      configFields: [
        { key: "botToken", label: t("deploy.bot_token", "Bot Token"), type: "password" },
      ],
    },
    {
      id: "whatsapp",
      name: "WhatsApp",
      icon: Phone,
      color: "text-green-400",
      bg: "bg-green-500/10",
      border: "border-green-500/30",
      description: t("deploy.whatsapp_desc", "Connect via WhatsApp Business API"),
      configFields: [
        { key: "phoneNumber", label: t("deploy.phone_number", "Phone Number"), type: "text" },
        { key: "apiKey", label: t("deploy.api_key", "API Key"), type: "password" },
      ],
    },
    {
      id: "slack",
      name: "Slack",
      icon: Slack,
      color: "text-purple-400",
      bg: "bg-purple-500/10",
      border: "border-purple-500/30",
      description: t("deploy.slack_desc", "Connect via Slack App OAuth"),
      configFields: [
        { key: "workspace", label: t("deploy.workspace", "Workspace URL"), type: "text" },
        { key: "botToken", label: t("deploy.bot_token", "Bot Token"), type: "password" },
      ],
    },
    {
      id: "email",
      name: "Email",
      icon: Mail,
      color: "text-amber-400",
      bg: "bg-amber-500/10",
      border: "border-amber-500/30",
      description: t("deploy.email_desc", "Connect via SMTP/IMAP"),
      configFields: [
        { key: "smtpHost", label: t("deploy.smtp_host", "SMTP Host"), type: "text" },
        { key: "smtpPort", label: t("deploy.smtp_port", "SMTP Port"), type: "text" },
        { key: "email", label: t("deploy.email_address", "Email Address"), type: "text" },
        { key: "password", label: t("deploy.password", "Password"), type: "password" },
      ],
    },
    {
      id: "webhook",
      name: "Webhook",
      icon: Webhook,
      color: "text-zinc-400",
      bg: "bg-zinc-500/10",
      border: "border-zinc-500/30",
      description: t("deploy.webhook_desc", "Receive events via HTTP POST"),
      configFields: [],
      hasGenerator: true,
    },
  ];

  return (
    <div>
      <div className="mb-6">
        <h2 className="font-display text-xl sm:text-2xl text-white mb-2">
          {t("deploy.channel_setup", "Channel Setup")}
        </h2>
        <p className="font-mono text-xs text-zinc-500">
          {t("deploy.channel_setup_desc", "Connect your agent to communication channels. You can always add more later.")}
        </p>
      </div>

      <div className="space-y-3 mb-6">
        {channelList.map((ch) => {
          const Icon = ch.icon;
          const isConnected = channels[ch.id]?.connected;
          const isSelected = channels[ch.id]?.selected;
          const isTesting = channels[ch.id]?.testing;
          const isTested = channels[ch.id]?.tested;

          return (
            <div
              key={ch.id}
              className={`border transition-all ${
                isSelected ? ch.border : "border-zinc-800"
              } ${isSelected ? ch.bg : "bg-zinc-950/30"}`}
            >
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-3 sm:p-4 gap-3">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 flex items-center justify-center ${ch.bg}`}>
                    <Icon className={`w-5 h-5 ${ch.color}`} />
                  </div>
                  <div>
                    <h3 className="font-display text-white text-sm">{ch.name}</h3>
                    <p className="font-mono text-[10px] text-zinc-500">{ch.description}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {isConnected && (
                    <span className="font-mono text-[10px] px-2 py-1 bg-emerald-500/20 text-emerald-400 uppercase tracking-wider flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" />
                      {t("deploy.connected", "Connected")}
                    </span>
                  )}
                  {isTested && !isConnected && (
                    <span className="font-mono text-[10px] px-2 py-1 bg-blue-500/20 text-blue-400 uppercase tracking-wider">
                      {t("deploy.tested", "Tested")}
                    </span>
                  )}
                  <button
                    onClick={() => onToggleChannel(ch.id)}
                    className={`px-4 py-2 font-mono text-xs uppercase tracking-wider border transition-colors ${
                      isSelected
                        ? "border-amber-500/50 bg-amber-500/10 text-amber-400"
                        : "border-zinc-700 text-zinc-400 hover:border-zinc-600"
                    }`}
                  >
                    {isSelected ? t("deploy.selected", "Selected") : t("deploy.select", "Select")}
                  </button>
                </div>
              </div>

              {/* Channel Config Form */}
              {isSelected && (
                <div className="border-t border-zinc-800 p-3 sm:p-4">
                  {ch.hasGenerator ? (
                    <div>
                      <label className="block font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-2">
                        {t("deploy.webhook_url", "Webhook URL")}
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          readOnly
                          value={`https://api.maxoscloud.com/v1/webhook/${ch.id}_${Math.random().toString(36).substring(2, 10)}`}
                          className="flex-1 bg-transparent border border-zinc-700 px-3 py-2 font-mono text-xs text-zinc-300"
                        />
                        <button
                          onClick={() => toast.success(t("deploy.copied", "Copied to clipboard!"))}
                          className="p-2 border border-zinc-700 hover:border-zinc-600 transition-colors"
                        >
                          <Copy className="w-4 h-4 text-zinc-400" />
                        </button>
                      </div>
                      <p className="font-mono text-[10px] text-zinc-600 mt-2 flex items-center gap-1">
                        <Info className="w-3 h-3" />
                        {t("deploy.webhook_info", "Use this URL to receive events from external services.")}
                      </p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {ch.configFields.map((field) => (
                        <div key={field.key}>
                          <label className="block font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-1">
                            {field.label}
                          </label>
                          <input
                            type={field.type}
                            placeholder={field.label}
                            className="w-full bg-transparent border border-zinc-700 px-3 py-2 font-mono text-xs text-white placeholder:text-zinc-600 focus:border-amber-500/50 focus:outline-none"
                          />
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="flex items-center gap-2 mt-3">
                    <button
                      onClick={() => onTestChannel(ch.id)}
                      disabled={isTesting}
                      className="flex items-center gap-1.5 px-3 py-1.5 border border-zinc-700 font-mono text-[11px] text-zinc-400 uppercase tracking-wider hover:border-amber-500/30 hover:text-amber-400 transition-colors disabled:opacity-50"
                    >
                      {isTesting ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <Zap className="w-3 h-3" />
                      )}
                      {isTesting ? t("deploy.testing", "Testing...") : t("deploy.test_connection", "Test Connection")}
                    </button>
                    {isTested && !isConnected && (
                      <button
                        onClick={() => onToggleChannel(ch.id, true)}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-500/20 border border-emerald-500/30 font-mono text-[11px] text-emerald-400 uppercase tracking-wider hover:bg-emerald-500/30 transition-colors"
                      >
                        <CheckCircle2 className="w-3 h-3" />
                        {t("deploy.connect", "Connect")}
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="flex flex-col sm:flex-row justify-between gap-2">
        <button
          onClick={onBack}
          className="flex items-center gap-2 px-6 py-3 border border-zinc-700 font-mono text-sm text-zinc-400 uppercase tracking-wider hover:border-zinc-600 transition-colors"
        >
          <ChevronLeft className="w-4 h-4" />
          {t("deploy.back", "Back")}
        </button>
        <button
          onClick={onNext}
          className="flex items-center gap-2 px-6 py-3 bg-amber-500 text-black font-mono text-sm font-bold uppercase tracking-wider hover:bg-amber-400 transition-colors"
        >
          {t("deploy.continue", "Continue")}
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

// ─── Step 5: Review & Deploy ─────────────────────────────────────────────────
function ReviewDeploy({ agent, plan, config, channels, billingCycle, onDeploy, onBack, isDeploying }) {
  const { t } = useTranslation();
  const [termsAccepted, setTermsAccepted] = useState(false);

  const isAnnual = billingCycle === "annual";
  const price = isAnnual ? plan.annualPrice : plan.monthlyPrice;
  const selectedChannels = Object.entries(channels)
    .filter(([, v]) => v.selected)
    .map(([k]) => k);

  return (
    <div>
      <div className="mb-6">
        <h2 className="font-display text-xl sm:text-2xl text-white mb-2">
          {t("deploy.review_deploy", "Review & Deploy")}
        </h2>
        <p className="font-mono text-xs text-zinc-500">
          {t("deploy.review_deploy_desc", "Review your configuration before deploying your agent.")}
        </p>
      </div>

      <div className="space-y-3 sm:space-y-4 mb-6">
        {/* Agent Summary */}
        <div className="border border-zinc-800 bg-zinc-950/30 p-3 sm:p-4">
          <h3 className="font-mono text-[10px] uppercase tracking-wider text-amber-500 mb-3">
            {t("deploy.agent", "Agent")}
          </h3>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 border border-zinc-700 flex items-center justify-center text-2xl bg-zinc-950/50">
              {config.avatar || "🤖"}
            </div>
            <div>
              <p className="font-display text-white">{config.agentName || agent?.name}</p>
              <p className="font-mono text-[11px] text-zinc-500">
                {agent?.name} · {config.personality || t("deploy.pers_professional", "Professional")}
              </p>
            </div>
          </div>
        </div>

        {/* Plan Summary */}
        <div className="border border-zinc-800 bg-zinc-950/30 p-3 sm:p-4">
          <h3 className="font-mono text-[10px] uppercase tracking-wider text-amber-500 mb-3">
            {t("deploy.plan", "Plan")}
          </h3>
          <div className="flex items-center justify-between">
            <div>
              <p className="font-display text-white">{plan.name}</p>
              <p className="font-mono text-[11px] text-zinc-500">
                {isAnnual ? t("deploy.billed_annually", "Billed annually") : t("deploy.billed_monthly", "Billed monthly")}
              </p>
            </div>
            <div className="text-right">
              <p className="font-display text-2xl text-white">${price}</p>
              <p className="font-mono text-[10px] text-zinc-500">/{t("deploy.month", "mo")}</p>
            </div>
          </div>
        </div>

        {/* Channels Summary */}
        <div className="border border-zinc-800 bg-zinc-950/30 p-3 sm:p-4">
          <h3 className="font-mono text-[10px] uppercase tracking-wider text-amber-500 mb-3">
            {t("deploy.channels", "Channels")} ({selectedChannels.length})
          </h3>
          {selectedChannels.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {selectedChannels.map((ch) => (
                <span
                  key={ch}
                  className="font-mono text-[11px] px-3 py-1 border border-zinc-700 text-zinc-300 uppercase"
                >
                  {ch}
                </span>
              ))}
            </div>
          ) : (
            <p className="font-mono text-[11px] text-zinc-500">
              {t("deploy.no_channels", "No channels selected. You can add channels later.")}
            </p>
          )}
        </div>

        {/* Config Summary */}
        <div className="border border-zinc-800 bg-zinc-950/30 p-3 sm:p-4">
          <h3 className="font-mono text-[10px] uppercase tracking-wider text-amber-500 mb-3">
            {t("deploy.configuration", "Configuration")}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 font-mono text-[11px]">
            <div>
              <span className="text-zinc-500">{t("deploy.timezone", "Timezone")}:</span>{" "}
              <span className="text-zinc-300">{config.timezone || "—"}</span>
            </div>
            <div>
              <span className="text-zinc-500">{t("deploy.languages", "Languages")}:</span>{" "}
              <span className="text-zinc-300">{(config.languages || []).join(", ") || "—"}</span>
            </div>
            {config.greeting && (
              <div className="col-span-2">
                <span className="text-zinc-500">{t("deploy.greeting", "Greeting")}:</span>{" "}
                <span className="text-zinc-300">{config.greeting}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Terms */}
      <div className="flex items-start gap-3 mb-6 p-3 sm:p-4 border border-zinc-800 bg-zinc-950/30">
        <button
          onClick={() => setTermsAccepted(!termsAccepted)}
          className={`w-5 h-5 border flex items-center justify-center shrink-0 mt-0.5 transition-colors ${
            termsAccepted ? "border-amber-500 bg-amber-500/20" : "border-zinc-600"
          }`}
        >
          {termsAccepted && <Check className="w-3 h-3 text-amber-400" />}
        </button>
        <p className="font-mono text-[11px] text-zinc-400 leading-relaxed">
          {t("deploy.terms_text", "I agree to the Terms of Service and Privacy Policy. I understand that my agent will be deployed to a cloud VM and billed according to the selected plan. I can cancel anytime within 7 days for a full refund.")}
        </p>
      </div>

      <div className="flex justify-between">
        <button
          onClick={onBack}
          disabled={isDeploying}
          className="flex items-center gap-2 px-6 py-3 border border-zinc-700 font-mono text-sm text-zinc-400 uppercase tracking-wider hover:border-zinc-600 transition-colors disabled:opacity-30"
        >
          <ChevronLeft className="w-4 h-4" />
          {t("deploy.back", "Back")}
        </button>
        <button
          onClick={onDeploy}
          disabled={!termsAccepted || isDeploying}
          className="flex items-center gap-2 w-full sm:w-auto px-8 py-3 bg-amber-500 text-black font-mono text-sm font-bold uppercase tracking-wider hover:bg-amber-400 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
        >
          {isDeploying ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              {t("deploy.deploying", "Deploying...")}
            </>
          ) : (
            <>
              <Rocket className="w-4 h-4" />
              {t("deploy.deploy_now", "Deploy Now")}
            </>
          )}
        </button>
      </div>
    </div>
  );
}

// ─── Deploy Progress ─────────────────────────────────────────────────────────
function DeployProgress({ status, error, onRetry, onViewDashboard }) {
  const { t } = useTranslation();

  const steps = [
    { id: "provisioning", label: t("deploy.provisioning", "Provisioning VM"), icon: Zap },
    { id: "configuring", label: t("deploy.configuring", "Configuring Agent"), icon: Settings },
    { id: "connecting", label: t("deploy.connecting", "Connecting Channels"), icon: Plug },
    { id: "ready", label: t("deploy.ready", "Agent Ready"), icon: CheckCircle2 },
  ];

  const currentIndex = steps.findIndex((s) => s.id === status);

  if (status === "error") {
    return (
      <div className="text-center py-6 sm:py-12">
        <div className="w-16 h-16 mx-auto mb-4 border border-red-500/30 bg-red-500/10 flex items-center justify-center">
          <XCircle className="w-8 h-8 text-red-400" />
        </div>
        <h2 className="font-display text-xl text-white mb-2">
          {t("deploy.deploy_failed", "Deployment Failed")}
        </h2>
        <p className="font-mono text-xs text-zinc-500 mb-6 max-w-md mx-auto">
          {error || t("deploy.deploy_error_desc", "An error occurred during deployment. Please try again or contact support.")}
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-2">
          <button
            onClick={onRetry}
            className="flex items-center gap-2 px-6 py-3 bg-amber-500 text-black font-mono text-sm font-bold uppercase tracking-wider hover:bg-amber-400 transition-colors"
          >
            <RotateCcw className="w-4 h-4" />
            {t("deploy.retry", "Retry")}
          </button>
          <button
            onClick={onViewDashboard}
            className="flex items-center gap-2 px-6 py-3 border border-zinc-700 font-mono text-sm text-zinc-400 uppercase tracking-wider hover:border-zinc-600 transition-colors"
          >
            {t("deploy.view_dashboard", "View Dashboard")}
          </button>
        </div>
      </div>
    );
  }

  if (status === "success") {
    return (
      <div className="text-center py-6 sm:py-12">
        <div className="w-16 h-16 mx-auto mb-4 border border-emerald-500/30 bg-emerald-500/10 flex items-center justify-center">
          <CheckCircle2 className="w-8 h-8 text-emerald-400" />
        </div>
        <h2 className="font-display text-xl text-white mb-2">
          {t("deploy.deploy_success", "Agent Deployed!")}
        </h2>
        <p className="font-mono text-xs text-zinc-500 mb-2">
          {t("deploy.deploy_success_desc", "Your AI agent is now live and ready to work.")}
        </p>
        <p className="font-mono text-[10px] text-amber-400 mb-6">
          {t("deploy.estimated_time", "Completed in ~45 seconds")}
        </p>

        <div className="border border-zinc-800 bg-zinc-950/30 p-4 mb-6 max-w-md mx-auto text-left">
          <h3 className="font-mono text-[10px] uppercase tracking-wider text-amber-500 mb-3">
            {t("deploy.post_deploy_checklist", "Post-Deploy Checklist")}
          </h3>
          <ul className="space-y-2">
            {[
              t("deploy.checklist_1", "Send a test message to verify the agent responds"),
              t("deploy.checklist_2", "Check channel connections are active"),
              t("deploy.checklist_3", "Review agent knowledge base and customize responses"),
              t("deploy.checklist_4", "Set up automations and workflows"),
            ].map((item, i) => (
              <li key={i} className="flex items-start gap-2">
                <Check className="w-3.5 h-3.5 text-emerald-400 mt-0.5 shrink-0" />
                <span className="font-mono text-[11px] text-zinc-400">{item}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="flex items-center justify-center gap-3">
          <button
            onClick={onViewDashboard}
            className="flex items-center gap-2 px-6 py-3 bg-amber-500 text-black font-mono text-sm font-bold uppercase tracking-wider hover:bg-amber-400 transition-colors"
          >
            <ExternalLink className="w-4 h-4" />
            {t("deploy.open_agent_dashboard", "Open Agent Dashboard")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="py-8">
      <div className="text-center mb-8">
        <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center">
          <Loader2 className="w-8 h-8 text-amber-400 animate-spin" />
        </div>
        <h2 className="font-display text-xl text-white mb-1">
          {t("deploy.deploying_agent", "Deploying Your Agent")}
        </h2>
        <p className="font-mono text-[11px] text-zinc-500">
          {t("deploy.deploying_desc", "This usually takes 30-60 seconds. Please don't close this page.")}
        </p>
      </div>

      <div className="max-w-md mx-auto">
        {steps.map((step, i) => {
          const Icon = step.icon;
          const isActive = i === currentIndex;
          const isDone = i < currentIndex;

          return (
            <div key={step.id} className="flex items-center gap-3 mb-4">
              <div
                className={`w-8 h-8 flex items-center justify-center border ${
                  isDone
                    ? "border-emerald-400 bg-emerald-400/10"
                    : isActive
                    ? "border-amber-400 bg-amber-400/10"
                    : "border-zinc-700"
                }`}
              >
                {isDone ? (
                  <Check className="w-4 h-4 text-emerald-400" />
                ) : isActive ? (
                  <Loader2 className="w-4 h-4 text-amber-400 animate-spin" />
                ) : (
                  <Icon className="w-4 h-4 text-zinc-600" />
                )}
              </div>
              <span
                className={`font-mono text-xs uppercase tracking-wider ${
                  isDone ? "text-emerald-400" : isActive ? "text-amber-400" : "text-zinc-600"
                }`}
              >
                {step.label}
              </span>
              {isActive && (
                <span className="font-mono text-[10px] text-zinc-500 ml-auto">
                  {t("deploy.in_progress", "In progress...")}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Main DeployWizard Export ────────────────────────────────────────────────
export default function DeployWizard({ initialAgent }) {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(initialAgent || null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [billingCycle, setBillingCycle] = useState("monthly");
  const [promoCode, setPromoCode] = useState("");
  const [agentConfig, setAgentConfig] = useState({
    agentName: "",
    avatar: "🤖",
    personality: "professional",
    timezone: "UTC",
    languages: ["en"],
    greeting: "",
  });
  const [channels, setChannels] = useState({});
  const [deployState, setDeployState] = useState("idle"); // idle, deploying, success, error
  const [deployError, setDeployError] = useState(null);

  const markComplete = (step) => {
    setCompletedSteps((prev) => (prev.includes(step) ? prev : [...prev, step]));
  };

  const goToStep = (step) => {
    setCurrentStep(step);
  };

  const handleDeploy = () => {
    setDeployState("deploying");
    setDeployError(null);

    // Simulate deploy process
    const steps = ["provisioning", "configuring", "connecting", "ready"];
    let i = 0;
    const interval = setInterval(() => {
      i++;
      if (i < steps.length) {
        setDeployState(steps[i]);
        // Use a different state approach - we track progress separately
      } else {
        clearInterval(interval);
        setDeployState("success");
      }
    }, 1500);

    // Track progress through deploying sub-states
    setDeployState("provisioning");
    setTimeout(() => setDeployState("configuring"), 1500);
    setTimeout(() => setDeployState("connecting"), 3000);
    setTimeout(() => setDeployState("ready"), 4500);
    setTimeout(() => {
      setDeployState("success");
      clearInterval(interval);
    }, 5000);
  };

  const handleRetry = () => {
    setDeployState("idle");
    setCurrentStep(1);
  };

  // If deploying or deployed, show progress
  if (deployState !== "idle") {
    return (
      <div className="max-w-2xl mx-auto">
        <DeployProgress
          status={deployState}
          error={deployError}
          onRetry={handleRetry}
          onViewDashboard={() => (window.location.href = "/dashboard")}
        />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto">
      <StepIndicator
        currentStep={currentStep}
        completedSteps={completedSteps}
        onStepClick={goToStep}
      />

      <div className="border border-zinc-800 bg-zinc-950/30 p-4 sm:p-6">
        {currentStep === 1 && (
          <AgentSelection
            selectedAgent={selectedAgent}
            onSelect={setSelectedAgent}
            onNext={() => {
              markComplete(1);
              setCurrentStep(2);
            }}
          />
        )}

        {currentStep === 2 && (
          <PlanSelection
            selectedPlan={selectedPlan}
            billingCycle={billingCycle}
            promoCode={promoCode}
            onSelectPlan={setSelectedPlan}
            onToggleBilling={() => setBillingCycle(billingCycle === "monthly" ? "annual" : "monthly")}
            onPromoChange={setPromoCode}
            onNext={() => {
              markComplete(2);
              setCurrentStep(3);
            }}
            onBack={() => setCurrentStep(1)}
          />
        )}

        {currentStep === 3 && (
          <AgentConfiguration
            config={agentConfig}
            onChange={setAgentConfig}
            onNext={() => {
              markComplete(3);
              setCurrentStep(4);
            }}
            onBack={() => setCurrentStep(2)}
          />
        )}

        {currentStep === 4 && (
          <ChannelSetup
            channels={channels}
            onToggleChannel={(id, forceConnect) => {
              setChannels((prev) => {
                const current = prev[id] || { selected: false, connected: false, testing: false, tested: false };
                if (forceConnect) {
                  return { ...prev, [id]: { ...current, selected: true, connected: true, tested: true } };
                }
                return { ...prev, [id]: { ...current, selected: !current.selected } };
              });
            }}
            onTestChannel={(id) => {
              setChannels((prev) => ({
                ...prev,
                [id]: { ...prev[id], testing: true },
              }));
              setTimeout(() => {
                setChannels((prev) => ({
                  ...prev,
                  [id]: { ...prev[id], testing: false, tested: true },
                }));
                toast.success(t("deploy.test_success", "Connection test successful!"));
              }, 2000);
            }}
            onNext={() => {
              markComplete(4);
              setCurrentStep(5);
            }}
            onBack={() => setCurrentStep(3)}
          />
        )}

        {currentStep === 5 && (
          <ReviewDeploy
            agent={selectedAgent}
            plan={selectedPlan}
            config={agentConfig}
            channels={channels}
            billingCycle={billingCycle}
            onDeploy={handleDeploy}
            onBack={() => setCurrentStep(4)}
            isDeploying={false}
          />
        )}
      </div>
    </div>
  );
}
